import { useEffect, useState } from 'react';
import type { FC } from 'react';

type SupportedLocale = 'en' | 'zh-Hans';

type LocaleCopy = {
  skipLabel: string;
  switcher: {
    label: string;
    options: Record<SupportedLocale, string>;
  };
  title: string;
  tagline: string;
  intros: string[];
  meta: { label: string; value: string }[];
  checklistTitle: string;
  checklist: string[];
  faqTitle: string;
  faqs: { title: string; body: string }[];
  footer: string[];
};

const copy: Record<SupportedLocale, LocaleCopy> = {
  en: {
    skipLabel: 'Skip to main content',
    switcher: {
      label: 'Language',
      options: {
        en: 'English',
        'zh-Hans': '简体中文'
      }
    },
    title: 'CycleTask Support',
    tagline: 'Keep gentle rituals in flow',
    intros: [
      'If you run into issues with sync, notifications, or the purchase flow, email us anytime.',
      "We'll respond as soon as possible with tailored troubleshooting steps."
    ],
    meta: [
      {
        label: 'Response cadence',
        value: 'We reply as quickly as possible'
      },
      {
        label: 'Languages',
        value: 'English · Simplified Chinese'
      }
    ],
    checklistTitle: 'Email checklist',
    checklist: [
      'Steps leading up to the issue and what you expected to happen.',
      'Device model, iOS version, and the CycleTask build number.',
      'Screenshots or screen recordings that show the issue.'
    ],
    faqTitle: 'FAQ',
    faqs: [
      {
        title: 'Free plan limits',
        body: 'The free tier supports five active rituals. A one-time unlock lifts the limit and enables private iCloud sync.'
      },
      {
        title: 'Shortcuts & widgets',
        body: 'CycleTask includes interactive widgets and Shortcuts actions for "Create", "Complete", and "Get Upcoming" rituals.'
      }
    ],
    footer: ['© {year} CycleTask · Crafted with care']
  },
  'zh-Hans': {
    skipLabel: '跳至主要内容',
    switcher: {
      label: '界面语言',
      options: {
        en: 'English',
        'zh-Hans': '简体中文'
      }
    },
    title: 'CycleTask 技术支持',
    tagline: '让温柔的日常保持节奏',
    intros: [
      '如果你在同步、通知或购买流程中遇到问题，欢迎随时发送邮件告诉我们。',
      '我们会尽快回复，并提供针对性的排查建议。'
    ],
    meta: [
      {
        label: '回复节奏',
        value: '收到邮件后我们会尽快联系你'
      },
      {
        label: '支持语言',
        value: 'English · 简体中文'
      }
    ],
    checklistTitle: '邮件中请包含以下信息',
    checklist: [
      '问题出现前后的具体操作，以及你期待的结果。',
      '设备型号、iOS 版本与 CycleTask 的版本号。',
      '如可行，请附上截图或录屏，帮助我们快速复现。'
    ],
    faqTitle: '常见问题',
    faqs: [
      {
        title: '免费版限制',
        body: '免费版可管理 5 个活跃节奏；一次性解锁即可无限扩容并启用私密 iCloud 同步。'
      },
      {
        title: '快捷指令与小组件',
        body: 'CycleTask 支持互动小组件与 Create / Complete / Get Upcoming 快捷指令动作。'
      }
    ],
    footer: ['© {year} CycleTask · 以关怀打磨每一次循环']
  }
};

const detectLocale = (): SupportedLocale => {
  if (typeof navigator === 'undefined') {
    return 'en';
  }

  const candidates = navigator.languages ?? [navigator.language];
  const matched = candidates.find((language) => language?.toLowerCase().startsWith('zh'));
  return matched ? 'zh-Hans' : 'en';
};

const App: FC = () => {
  const [locale, setLocale] = useState<SupportedLocale>(() => detectLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const strings = copy[locale];
  const currentYear = new Date().getFullYear();
  const localeOptions: SupportedLocale[] = ['en', 'zh-Hans'];

  const handleLocaleChange = (nextLocale: SupportedLocale) => {
    if (nextLocale !== locale) {
      setLocale(nextLocale);
    }
  };

  return (
    <div className="page" data-locale={locale}>
      <a className="skip-link" href="#main-content">
        {strings.skipLabel}
      </a>
      <main id="main-content" className="support">
        <section className="support__card" aria-labelledby="support-title">
          <header className="support__header">
            <div className="support__topbar">
              <span className="support__badge" aria-label="CycleTask">
                CycleTask
              </span>
              <div
                className="support__switcher"
                role="group"
                aria-label={strings.switcher.label}
              >
                {localeOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`support__switcher-button${
                      locale === option ? ' support__switcher-button--active' : ''
                    }`}
                    onClick={() => handleLocaleChange(option)}
                    aria-pressed={locale === option}
                  >
                    {strings.switcher.options[option]}
                  </button>
                ))}
              </div>
            </div>
            <p className="support__tagline">{strings.tagline}</p>
          </header>

          <h1 id="support-title">{strings.title}</h1>
          {strings.intros.map((paragraph) => (
            <p key={paragraph} className="support__intro">
              {paragraph}
            </p>
          ))}

          <a className="support__cta" href="mailto:cycletask@zach.ren">
            cycletask@zach.ren
          </a>

          <dl className="support__meta">
            {strings.meta.map(({ label, value }) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>

          <section aria-labelledby="quick-steps" className="support__section">
            <h2 id="quick-steps">{strings.checklistTitle}</h2>
            <ol className="support__list">
              {strings.checklist.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </section>

          <section aria-labelledby="faq" className="support__section support__section--secondary">
            <h2 id="faq">{strings.faqTitle}</h2>
            <ul className="support__faq">
              {strings.faqs.map(({ title, body }) => (
                <li key={title}>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </li>
              ))}
            </ul>
          </section>
        </section>

        <footer className="support__footer">
          {strings.footer.map((line) => (
            <p key={line}>{line.replace('{year}', String(currentYear))}</p>
          ))}
        </footer>
      </main>
    </div>
  );
};

export default App;
